import { getActiveStore } from "@/lib/scope";
import WriteButton from "@/components/WriteButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Trash2, ClipboardCheck, ArrowLeftRight, History, PackagePlus, Upload, FileDown, Loader2, Store, Activity } from "lucide-react";
import { useListNav } from "@/lib/useListNav";

export default function Stok() {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [opnames, setOpnames] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [movements, setMovements] = useState([]);
  const activeStore = getActiveStore() || "";

  const load = async () => {
    try {
      const [pr, st, op, tr, mv] = await Promise.all([
        api.get("/products"), api.get("/stores"), api.get("/opname"), api.get("/transfers"), api.get("/stock-movements"),
      ]);
      setProducts(pr.data); setStores(st.data); setOpnames(op.data); setTransfers(tr.data); setMovements(mv.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const activeName = stores.find((s) => s.id === activeStore)?.name || "-";

  return (
    <>
      <PageHeader title="Stok" subtitle="Opname, Transfer antar cabang & Riwayat">
        <div className="flex items-center gap-2 rounded-xl bg-[#F7F9FB] border border-slate-300 px-3 py-2" data-testid="active-store-badge">
          <Store className="h-4 w-4 text-emerald-600" />
          <span className="text-xs text-slate-500">Cabang aktif:</span>
          <span className="text-sm font-semibold text-slate-800">{activeName}</span>
        </div>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5">
        <Tabs defaultValue="opname">
          <TabsList className="bg-[#F7F9FB] border border-slate-300">
            <TabsTrigger value="opname" data-testid="tab-opname" className="gap-1"><ClipboardCheck className="h-4 w-4" /> Opname</TabsTrigger>
            <TabsTrigger value="transfer" data-testid="tab-transfer" className="gap-1"><ArrowLeftRight className="h-4 w-4" /> Transfer</TabsTrigger>
            <TabsTrigger value="riwayat" data-testid="tab-riwayat" className="gap-1"><History className="h-4 w-4" /> Riwayat</TabsTrigger>
            <TabsTrigger value="pergerakan" data-testid="tab-pergerakan" className="gap-1"><Activity className="h-4 w-4" /> Pergerakan</TabsTrigger>
          </TabsList>

          <TabsContent value="opname" className="mt-5">
            <OpnameTab products={products} activeName={activeName} onDone={load} />
          </TabsContent>
          <TabsContent value="transfer" className="mt-5">
            <TransferTab products={products} stores={stores} activeStore={activeStore} activeName={activeName} onDone={load} />
          </TabsContent>
          <TabsContent value="riwayat" className="mt-5">
            <RiwayatTab opnames={opnames} transfers={transfers} />
          </TabsContent>
          <TabsContent value="pergerakan" className="mt-5">
            <MovementTab movements={movements} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function ProductPicker({ products, exclude, onPick, testid }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(
    () => (search ? products.filter((p) => (p.name.toLowerCase().includes(search.toLowerCase()) || (p.barcode || "").includes(search)) && !exclude.includes(p.id)).slice(0, 6) : []),
    [search, products, exclude]
  );
  const nav = useListNav(filtered, (p) => { onPick(p); setSearch(""); });
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <Input data-testid={testid} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={nav.onInputKeyDown} placeholder="Cari & tambahkan barang…" className="pl-10 bg-[#F7F9FB] border-slate-300" />
      {filtered.length > 0 && (
        <div ref={nav.listRef} className="absolute z-10 mt-1 w-full rounded-lg border border-slate-300 bg-[#F7F9FB] shadow-xl overflow-hidden">
          {filtered.map((p, idx) => (
            <button key={p.id} data-testid={`pick-${p.id}`} data-nav-index={idx} onMouseEnter={() => nav.setActive(idx)} onClick={() => { onPick(p); setSearch(""); }} className={`w-full text-left px-3 py-2 flex items-center justify-between ${idx === nav.active ? "bg-slate-200" : "hover:bg-slate-200"}`}>
              <span className="text-sm">{p.name}</span>
              <span className="text-xs text-slate-500 font-mono">stok {p.stock}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OpnameTab({ products, activeName, onDone }) {
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const addLine = (p) => setLines((prev) => (prev.find((l) => l.product_id === p.id) ? prev : [...prev, { product_id: p.id, name: p.name, before: p.stock, counted: p.stock, cost: p.cost || 0 }]));
  const setCounted = (i, v) => setLines((prev) => prev.map((l, x) => (x === i ? { ...l, counted: v } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, x) => x !== i));

  const totals = useMemo(() => lines.reduce((a, l) => {
    const d = Number(l.counted || 0) - Number(l.before || 0);
    return { qty: a.qty + d, value: a.value + d * Number(l.cost || 0) };
  }, { qty: 0, value: 0 }), [lines]);

  const submit = async () => {
    if (lines.length === 0) return toast.error("Tambahkan minimal 1 barang");
    setSaving(true);
    try {
      const { data } = await api.post("/opname", { items: lines.map((l) => ({ product_id: l.product_id, counted: Number(l.counted || 0) })), note: note || null });
      toast.success(`Opname ${data.opname_no} tersimpan · selisih ${data.total_diff_qty} pcs (${rupiah(data.total_diff_value)})`);
      setLines([]); setNote(""); onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-500">Sesuaikan stok fisik untuk cabang <b className="text-slate-700">{activeName}</b>. Sistem akan mengganti stok = jumlah fisik dan mencatat selisih.</div>
      <ProductPicker products={products} exclude={lines.map((l) => l.product_id)} onPick={addLine} testid="opname-search" />
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F9FB] text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left p-2 pl-3">Barang</th>
              <th className="text-center p-2 w-24">Stok Sistem</th>
              <th className="text-center p-2 w-28">Stok Fisik</th>
              <th className="text-right p-2 w-28">Selisih</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody data-testid="opname-lines">
            {lines.map((l, i) => {
              const diff = Number(l.counted || 0) - Number(l.before || 0);
              return (
                <tr key={i} className="border-t border-slate-200">
                  <td className="p-2 pl-3">{l.name}</td>
                  <td className="p-2 text-center font-mono text-slate-500">{l.before}</td>
                  <td className="p-2"><Input data-testid={`opname-counted-${i}`} type="number" value={l.counted} onChange={(e) => setCounted(i, e.target.value)} className="h-9 text-center font-mono bg-slate-100 border-slate-300" /></td>
                  <td className={`p-2 text-right font-mono font-bold ${diff > 0 ? "text-emerald-600" : diff < 0 ? "text-red-600" : "text-slate-500"}`}>{diff > 0 ? `+${diff}` : diff}</td>
                  <td className="p-2 text-center"><button data-testid={`opname-remove-${i}`} onClick={() => removeLine(i)} className="text-red-600 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              );
            })}
            {lines.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500"><PackagePlus className="h-6 w-6 mx-auto mb-1" /> Belum ada barang</td></tr>}
          </tbody>
        </table>
      </div>
      <Textarea data-testid="opname-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan opname (opsional)" className="bg-[#F7F9FB] border-slate-300 min-h-[38px]" rows={1} />
      <div className="flex items-center justify-between rounded-xl bg-[#F7F9FB] border border-slate-300 px-4 py-3">
        <span className="text-slate-500 text-sm">Total selisih: <b data-testid="opname-total-qty" className="text-slate-700">{totals.qty} pcs</b> · <span data-testid="opname-total-value" className={totals.value < 0 ? "text-red-600" : "text-emerald-600"}>{rupiah(totals.value)}</span></span>
        <WriteButton data-testid="save-opname-btn" onClick={submit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold">Simpan Opname</WriteButton>
      </div>
    </div>
  );
}

function TransferTab({ products, stores, activeStore, activeName, onDone }) {
  const [toStore, setToStore] = useState("");
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const destStores = stores.filter((s) => s.id !== activeStore);

  const addLine = (p) => setLines((prev) => (prev.find((l) => l.product_id === p.id) ? prev : [...prev, { product_id: p.id, name: p.name, qty: 1, cost: p.cost || 0, stock: p.stock }]));
  const setQty = (i, v) => setLines((prev) => prev.map((l, x) => (x === i ? { ...l, qty: v } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, x) => x !== i));

  const totals = useMemo(() => lines.reduce((a, l) => ({ qty: a.qty + Number(l.qty || 0), value: a.value + Number(l.qty || 0) * Number(l.cost || 0) }), { qty: 0, value: 0 }), [lines]);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/transfers/template", { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data); link.download = "template_transfer.xlsx"; link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/transfers/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors?.length) toast.warning(`${data.errors.length} baris dilewati (produk tidak ditemukan)`);
      if (!data.items?.length) return toast.error("Tidak ada baris valid pada file Excel");
      const pmap = Object.fromEntries(products.map((p) => [p.id, p]));
      setLines(data.items.map((it) => ({ product_id: it.product_id, name: it.name, qty: Number(it.qty || 1), cost: Number(it.cost || 0), stock: pmap[it.product_id]?.stock })));
      toast.success(`${data.items.length} barang dimuat dari Excel`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setImporting(false); }
  };

  const submit = async () => {
    if (!toStore) return toast.error("Pilih cabang tujuan");
    if (lines.length === 0) return toast.error("Tambahkan minimal 1 barang");
    setSaving(true);
    try {
      const { data } = await api.post("/transfers", { to_store: toStore, items: lines.map((l) => ({ product_id: l.product_id, qty: Number(l.qty || 0) })), note: note || null });
      toast.success(`Transfer ${data.transfer_no} berhasil · ${data.total_qty} pcs (${rupiah(data.total_value)})`);
      setLines([]); setNote(""); onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-[#F7F9FB] border border-slate-300 px-3 py-2.5">
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Dari Cabang (asal)</div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mt-0.5"><Store className="h-4 w-4 text-amber-600" /> {activeName}</div>
        </div>
        <div className="space-y-1">
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Ke Cabang (tujuan)</div>
          <Select value={toStore} onValueChange={setToStore}>
            <SelectTrigger data-testid="transfer-dest-select" className="bg-[#F7F9FB] border-slate-300"><SelectValue placeholder="Pilih cabang tujuan" /></SelectTrigger>
            <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
              {destStores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input ref={fileRef} data-testid="transfer-import-file" type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
        <Button data-testid="transfer-template-btn" variant="outline" onClick={downloadTemplate} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB] gap-2"><FileDown className="h-4 w-4" /> Template</Button>
        <WriteButton data-testid="transfer-import-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="border-emerald-600/50 bg-transparent hover:bg-emerald-500/10 text-emerald-600 gap-2">
          {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import Excel
        </WriteButton>
      </div>

      <ProductPicker products={products} exclude={lines.map((l) => l.product_id)} onPick={addLine} testid="transfer-search" />

      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F9FB] text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left p-2 pl-3">Barang</th>
              <th className="text-center p-2 w-24">Stok Asal</th>
              <th className="text-center p-2 w-24">Qty Kirim</th>
              <th className="text-right p-2 w-28">Nilai</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody data-testid="transfer-lines">
            {lines.map((l, i) => (
              <tr key={i} className="border-t border-slate-200">
                <td className="p-2 pl-3">{l.name}</td>
                <td className="p-2 text-center font-mono text-slate-500">{l.stock ?? "-"}</td>
                <td className="p-2"><Input data-testid={`transfer-qty-${i}`} type="number" value={l.qty} onChange={(e) => setQty(i, e.target.value)} className="h-9 text-center font-mono bg-slate-100 border-slate-300" /></td>
                <td className="p-2 text-right font-mono text-blue-400">{rupiah(Number(l.qty || 0) * Number(l.cost || 0))}</td>
                <td className="p-2 text-center"><button data-testid={`transfer-remove-${i}`} onClick={() => removeLine(i)} className="text-red-600 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {lines.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500"><PackagePlus className="h-6 w-6 mx-auto mb-1" /> Belum ada barang</td></tr>}
          </tbody>
        </table>
      </div>

      <Textarea data-testid="transfer-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan transfer (opsional)" className="bg-[#F7F9FB] border-slate-300 min-h-[38px]" rows={1} />
      <div className="flex items-center justify-between rounded-xl bg-[#F7F9FB] border border-slate-300 px-4 py-3">
        <span className="text-slate-500 text-sm">Total: <b data-testid="transfer-total-qty" className="text-slate-700">{totals.qty} pcs</b> · <span data-testid="transfer-total-value" className="text-blue-400 font-mono">{rupiah(totals.value)}</span></span>
        <WriteButton data-testid="save-transfer-btn" onClick={submit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold">Kirim Transfer</WriteButton>
      </div>
    </div>
  );
}

function MovementTab({ movements }) {
  const fmt = (v) => (v ? new Date(v).toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");
  if (!movements.length) return <div className="p-10 text-center text-slate-500"><Activity className="h-8 w-8 mx-auto mb-2" /> Belum ada pergerakan stok</div>;
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="movement-table">
      <table className="w-full text-sm">
        <thead className="bg-[#F7F9FB] text-slate-500 text-xs uppercase">
          <tr>
            <th className="text-left p-2 pl-3 w-40">Waktu</th>
            <th className="text-left p-2">Barang</th>
            <th className="text-left p-2 hidden sm:table-cell">Keterangan</th>
            <th className="text-right p-2 w-24">Perubahan</th>
            <th className="text-left p-2 w-28 hidden md:table-cell">Oleh</th>
          </tr>
        </thead>
        <tbody>
          {movements.map((m) => (
            <tr key={m.id} data-testid={`movement-${m.id}`} className="border-t border-slate-200">
              <td className="p-2 pl-3 text-slate-500 whitespace-nowrap">{fmt(m.at)}</td>
              <td className="p-2">{m.product_name}</td>
              <td className="p-2 text-slate-500 hidden sm:table-cell">{m.note}</td>
              <td className={`p-2 text-right font-mono font-bold ${m.delta > 0 ? "text-emerald-600" : m.delta < 0 ? "text-red-600" : "text-slate-500"}`}>{m.delta > 0 ? `+${m.delta}` : m.delta}</td>
              <td className="p-2 text-slate-500 hidden md:table-cell">{m.by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RiwayatTab({ opnames, transfers }) {
  const rows = useMemo(() => {
    const all = [...opnames, ...transfers];
    all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return all;
  }, [opnames, transfers]);
  const [openId, setOpenId] = useState(null);
  const fmt = (v) => (v ? new Date(v).toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

  if (rows.length === 0) return <div className="p-10 text-center text-slate-500"><History className="h-8 w-8 mx-auto mb-2" /> Belum ada riwayat opname / transfer</div>;

  return (
    <div className="space-y-2" data-testid="riwayat-list">
      {rows.map((r) => {
        const isTr = r.type === "transfer";
        const open = openId === r.id;
        return (
          <div key={r.id} data-testid={`riwayat-${r.id}`} className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
            <button onClick={() => setOpenId(open ? null : r.id)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-100">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isTr ? "bg-blue-500/15 text-blue-300" : "bg-emerald-500/15 text-emerald-600"}`}>{isTr ? "TRANSFER" : "OPNAME"}</span>
                <span className="font-mono text-sm">{isTr ? r.transfer_no : r.opname_no}</span>
                <span className="text-xs text-slate-500 truncate hidden sm:inline">{isTr ? `${r.from_store_name} → ${r.to_store_name}` : r.store_name} · {fmt(r.created_at)}</span>
              </div>
              <div className="text-right shrink-0">
                {isTr ? (
                  <div className="text-sm font-mono text-blue-400">{r.total_qty} pcs · {rupiah(r.total_value)}</div>
                ) : (
                  <div className={`text-sm font-mono ${r.total_diff_value < 0 ? "text-red-600" : "text-emerald-600"}`}>{r.total_diff_qty > 0 ? "+" : ""}{r.total_diff_qty} pcs · {rupiah(r.total_diff_value)}</div>
                )}
              </div>
            </button>
            {open && (
              <div className="px-4 pb-3 border-t border-slate-200">
                <div className="text-[11px] text-slate-500 my-2">{isTr ? `${r.from_store_name} → ${r.to_store_name}` : `Cabang: ${r.store_name}`} · oleh {r.by} · {fmt(r.created_at)}{r.note ? ` · ${r.note}` : ""}</div>
                {r.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-t border-slate-200 py-1.5">
                    <span className="text-slate-600">{it.name}</span>
                    {isTr ? (
                      <span className="font-mono text-slate-600">{it.qty} pcs · {rupiah(it.value)}</span>
                    ) : (
                      <span className="font-mono text-slate-600">{it.before} → {it.counted} <span className={it.diff < 0 ? "text-red-600" : it.diff > 0 ? "text-emerald-600" : "text-slate-500"}>({it.diff > 0 ? "+" : ""}{it.diff})</span> · {rupiah(it.diff_value)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
